import { AiEntityProposalEntityType, AiEntityProposalType, AiFieldProposalStatus } from "src/generated/graphqlTypes";
import { PlanPackageAiProposalSummaryFragment } from "src/routes/libraries/plan-package-v2/planPackageAiProposalSummary.generated";

/** The plan package tabs that can review AI proposals today. */
export type AiProposalTab = "elevations" | "locations" | "options" | "programData";

type AiProposalTabSummary = { tab: AiProposalTab; pendingCount: number; title: string };

type EntityProposal = PlanPackageAiProposalSummaryFragment["entityProposals"][number];

export function summarizeAiProposal(
  proposal: PlanPackageAiProposalSummaryFragment | null | undefined,
): AiProposalTabSummary[] {
  // Drop entity proposals with nothing pending left to review
  const aeps = (proposal?.entityProposals ?? []).filter((aep) => !pendingFields(aep).isEmpty);
  return [
    summarizeElevations(ofEntityType(aeps, AiEntityProposalEntityType.Elevation)),
    summarizeLocations(ofEntityType(aeps, AiEntityProposalEntityType.ReadyPlanLocation)),
    summarizeOptions(ofEntityType(aeps, AiEntityProposalEntityType.ReadyPlanOption)),
    summarizeProgramData(ofEntityType(aeps, AiEntityProposalEntityType.ReadyPlanProgramData)),
  ].compact();
}

/**
 * Elevations are ReadyPlanOptions, but carry their own entity type so they summarize separately.
 * They are never proposed for removal, so there is no "removed" count to report.
 */
function summarizeElevations(aeps: EntityProposal[]): AiProposalTabSummary | undefined {
  if (aeps.isEmpty) return undefined;
  const counts = [
    [ofProposalType(aeps, AiEntityProposalType.Create).length, "new"],
    [ofProposalType(aeps, AiEntityProposalType.Update).length, "changed"],
  ] as const;
  const parts = counts.filter(([count]) => count > 0).map(([count, label]) => `${count} ${label}`);
  return {
    tab: "elevations",
    pendingCount: pendingCount(aeps),
    title: `Review elevation changes: ${parts.join(", ")}.`,
  };
}

/** A renumbered room is an UPDATE with a pending `roomNumber` proposal, called out separately. */
function summarizeLocations(aeps: EntityProposal[]): AiProposalTabSummary | undefined {
  if (aeps.isEmpty) return undefined;
  const updates = ofProposalType(aeps, AiEntityProposalType.Update);
  const renumbered = updates.filter((aep) => pendingFields(aep).some((fp) => fp.fieldName === "roomNumber"));
  const counts = [
    [ofProposalType(aeps, AiEntityProposalType.Create).length, "added"],
    [renumbered.length, "renumbered"],
    [updates.length - renumbered.length, "changed"],
    [ofProposalType(aeps, AiEntityProposalType.Delete).length, "removed"],
  ] as const;
  const parts = counts.filter(([count]) => count > 0).map(([count, label]) => `${count} ${label}`);
  return { tab: "locations", pendingCount: pendingCount(aeps), title: `Review location changes: ${parts.join(", ")}.` };
}

/** Options have no renumber analogue, and a created option reads as "new" rather than "added". */
function summarizeOptions(aeps: EntityProposal[]): AiProposalTabSummary | undefined {
  if (aeps.isEmpty) return undefined;
  const counts = [
    [ofProposalType(aeps, AiEntityProposalType.Create).length, "new"],
    [ofProposalType(aeps, AiEntityProposalType.Update).length, "changed"],
    [ofProposalType(aeps, AiEntityProposalType.Delete).length, "removed"],
  ] as const;
  const parts = counts.filter(([count]) => count > 0).map(([count, label]) => `${count} ${label}`);
  return { tab: "options", pendingCount: pendingCount(aeps), title: `Review option changes: ${parts.join(", ")}.` };
}

/** Program data is a single entity, so its proposals count by field rather than by row. */
function summarizeProgramData(aeps: EntityProposal[]): AiProposalTabSummary | undefined {
  const count = pendingCount(aeps);
  if (count === 0) return undefined;
  return {
    tab: "programData",
    pendingCount: count,
    title: `Review ${count} changed program value${count === 1 ? "" : "s"}.`,
  };
}

function ofEntityType(aeps: EntityProposal[], entityType: AiEntityProposalEntityType): EntityProposal[] {
  return aeps.filter((aep) => aep.entityType.code === entityType);
}

function ofProposalType(aeps: EntityProposal[], type: AiEntityProposalType): EntityProposal[] {
  return aeps.filter((aep) => aep.type.code === type);
}

function pendingFields(aep: EntityProposal) {
  return aep.fieldProposals.filter((fp) => fp.status.code === AiFieldProposalStatus.Pending);
}

function pendingCount(aeps: EntityProposal[]): number {
  return aeps.sum((aep) => pendingFields(aep).length);
}
